import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../api';
import type { ApplicationCapability, Environment, OnboardingPlan, DeploymentStrategy, PlanLifecycle, RepositoryAnalysis, Server, StorefrontDetection } from '../api';
import {
  
  humanize,
  
  
  labelFor,
  
  readinessStatusLabel,
  
  repositoryFallbackReasonLabel,
  serverStatusLabel,
  strategyDescription,
  strategyLabel,
} from '../labels';
import { ErrorNotice, Field, StatusBadge } from './ui';
import { Callout, Input, OptionCard, OptionGroup, SectionHeader, Select, StepList, Subsection, Table } from './primitives';
import { CatalogSearchSection } from './CatalogSearchSection';
import { QuietEmpty } from './project/ui';

interface OnboardingPlanCardProps {
  projectId: string;
  projectSlug: string;
  /** The environment the plan is generated for. Plans never span environments. */
  environment: Environment;
  servers: Server[];
  currentPlan?: OnboardingPlan | null;
  /** A headless project: the domain asked for is Magento's own hostname. */
  isHeadless?: boolean;
  onPlanUpdated: () => void;
  /** Starts the plan's setup run — or reopens the one already in flight. */
  onLaunchExecution?: (plan: OnboardingPlan) => void | Promise<void>;
  /** Opens an existing run of the plan. */
  onOpenExecution?: (executionId: string) => void;
}

const componentLabel: Record<string, string> = {
  reverse_proxy: 'Web entry point (reverse proxy)',
  php_runtime: 'PHP runtime',
  magento_app: 'Magento application',
  database: 'Database',
  redis: 'Redis (cache and sessions)',
  catalog_search: 'Catalog search',
  opensearch: 'Search service (OpenSearch)',
  rabbitmq: 'RabbitMQ (message queue)',
  http_cache: 'Varnish full page cache',
  cron: 'Magento cron and consumers',
  queue_consumers: 'Queue consumers',
  filesystem_media: 'Media files',
  frontend: 'Frontend application',
  tls: 'HTTPS certificate',
  domain_routing: 'Domain routing',
  docker_runtime: 'Docker',
  persistent_volumes: 'Persistent storage volumes',
  backups: 'Backups',
  monitoring: 'Monitoring',
};

const currentStateLabel: Record<string, string> = {
  application_provided: 'Runs inside Magento, no search service',
  external_service: 'Needs a search service',
  unresolved: 'Not determined',
  conflict: 'Store and repository disagree',
  operator_override: 'Set by hand in the Magento setup',
  host_opensearch_detected: 'Found on the server',
  external_search: 'Your external service',
  ports_available: 'Ports 80/443 are free',
  external_proxy_detected: 'Another web server already answers on 80/443',
  installed: 'Installed',
  containerized: 'Will run in a container',
  repository_code: 'From the repository',
  managed_container: 'Will run in a container',
  project_volumes: 'Per-environment volumes',
  magento_cron: 'Magento cron',
  unconfigured: 'Not set up yet',
  missing: 'Not installed',
  detected: 'Found on the server',
  not_provisioned: 'Not set up yet',
  provisioned: 'Running',
  agent_update_required: 'Needs a newer agent',
  external_ingress: 'Another web server owns 80/443',
  not_managed: 'Not managed by Commerce Ops',
};

/** componentDisplayName names the row; the search service row says which
 * engine it is instead of assuming OpenSearch. */
function componentDisplayName(comp: { component: string; details?: Record<string, any> }): string {
  if (comp.component === 'frontend' && typeof comp.details?.name === 'string' && comp.details.name) {
    return `Frontend application: ${comp.details.name}`;
  }
  if (comp.component === 'opensearch') {
    return comp.details?.engine === 'elasticsearch' ? 'Search service (Elasticsearch)' : 'Search service (OpenSearch)';
  }
  return componentLabel[comp.component] ?? humanize(comp.component);
}


/**
 * PerformanceStack lists what serves a page on the managed stack, read from
 * the plan's own components. Varnish appears only when the plan provisions
 * or keeps it (traditional Magento); a headless plan has no such component
 * and nothing says it is missing.
 */
export function PerformanceStack({ plan }: { plan: OnboardingPlan }) {
  if (plan.strategy !== 'commerceops_docker' || plan.components.length === 0) return null;
  const find = (type: string) => plan.components.find((c) => c.component === type);
  const runs = (type: string) => {
    const c = find(type);
    return !!c && (c.proposed_action === 'create' || c.proposed_action === 'reuse' || c.proposed_action === 'configure');
  };
  const proxy = find('reverse_proxy');
  const items: { name: string; note: string }[] = [];
  if (proxy && proxy.future_ownership === 'commerceops') {
    items.push({ name: 'OpenResty', note: 'Commerce Ops edge: HTTPS, deployment gate, routing' });
  }
  items.push({ name: 'PHP-FPM', note: 'Magento runs in the managed PHP runtime' });
  items.push({ name: 'OPcache', note: 'compiled PHP stays in memory' });
  if (runs('redis')) items.push({ name: 'Redis', note: 'cache and sessions' });
  if (runs('http_cache')) {
    const storage = Number(find('http_cache')?.details?.storage_mb ?? 0);
    items.push({ name: 'Varnish full page cache', note: `${storage ? `${storage} MB, ` : ''}VCL generated by Magento` });
  }
  return (
    <div data-testid="plan-performance-stack">
      <h3 className="plan-card__h3">Performance stack</h3>
      <ul className="perf-stack">
        {items.map((item) => (
          <li key={item.name} className="perf-stack__item">
            <span className="perf-stack__check" aria-hidden="true">✓</span>
            <span className="perf-stack__name">{item.name}</span>
            <span className="perf-stack__note muted">{item.note}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** planLifecycleOf reads the lifecycle the API derived; an older API that
 * does not send one falls back to the plan's review status. */
function planLifecycleOf(plan: OnboardingPlan): PlanLifecycle {
  if (plan.lifecycle) return plan.lifecycle;
  switch (plan.status) {
    case 'ready_for_review':
      return 'awaiting_approval';
    case 'approved':
      return 'approved';
    case 'needs_input':
    case 'blocked':
    case 'superseded':
      return plan.status;
    default:
      return 'draft';
  }
}

function formatWhen(iso?: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '';
}

/**
 * OnboardingPlanCard is where an environment gets its server: pick one,
 * let Commerce Ops plan what it would set up there (from the server check
 * and the Magento configuration), approve the plan, run it. Nothing touches
 * the server before the run.
 */
export function OnboardingPlanCard({
  projectId,
  environment,
  servers,
  currentPlan,
  isHeadless = false,
  onPlanUpdated,
  onLaunchExecution,
  onOpenExecution,
}: OnboardingPlanCardProps) {
  const [selectedServerId, setSelectedServerId] = useState<string>(
    currentPlan?.server_id || (servers.length > 0 && servers[0] ? servers[0].id : ''),
  );
  // The plan and the server list arrive after the card mounts on the project
  // page. Until the person picks a server, follow them: the plan's server
  // first, else the first server — so the selector's state matches what it
  // shows and "Plan again" re-reads the repository instead of asking for a
  // server that is visibly selected.
  useEffect(() => {
    if (selectedServerId) return;
    const next = currentPlan?.server_id || (servers.length > 0 && servers[0] ? servers[0].id : '');
    if (next) setSelectedServerId(next);
  }, [currentPlan?.server_id, servers, selectedServerId]);
  const [selectedStrategy, setSelectedStrategy] = useState<DeploymentStrategy>(currentPlan?.strategy || 'commerceops_docker');

  // The plan offers the hostname Magento is configured with; answering it
  // here changes Magento's base URL (the API saves it with the plan).
  const planDomain = currentPlan?.required_inputs.find((input) => input.id === 'production_domain');
  const [productionDomain, setProductionDomain] = useState<string>(
    currentPlan?.user_inputs?.production_domain || planDomain?.default_value || '',
  );

  const [generating, setGenerating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [launching, setLaunching] = useState(false);
  // Shown in the run area, next to the button that failed, not at the top
  // of a long plan the person has scrolled past.
  const [launchError, setLaunchError] = useState<Error | null>(null);
  const [actionError, setActionError] = useState<Error | null>(null);

  async function handleGeneratePlan(e?: FormEvent) {
    if (e) e.preventDefault();
    if (!selectedServerId) {
      setActionError(new Error('Choose the server this environment should run on first.'));
      return;
    }
    setGenerating(true);
    setActionError(null);
    try {
      const userInputs: Record<string, string> = { strategy: selectedStrategy };
      if (productionDomain.trim()) userInputs.production_domain = productionDomain.trim();
      await api.generateOnboardingPlan(projectId, {
        environment_id: environment.id,
        server_id: selectedServerId,
        user_inputs: userInputs,
      });
      onPlanUpdated();
    } catch (cause) {
      setActionError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setGenerating(false);
    }
  }

  async function handleApprovePlan() {
    if (!currentPlan) return;
    setApproving(true);
    setActionError(null);
    try {
      await api.approveOnboardingPlan(projectId, currentPlan.id);
      onPlanUpdated();
    } catch (cause) {
      setActionError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setApproving(false);
    }
  }

  async function handleLaunch() {
    if (!currentPlan || !onLaunchExecution) return;
    setLaunching(true);
    setLaunchError(null);
    try {
      await onLaunchExecution(currentPlan);
    } catch (cause) {
      setLaunchError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setLaunching(false);
    }
  }

  const selectedServer = servers.find((s) => s.id === selectedServerId);
  const serverNotReady = selectedServer && (selectedServer.status !== 'online' || selectedServer.readiness_status === 'not_run');
  const isApproved = currentPlan?.status === 'approved';
  const lifecycle = currentPlan ? planLifecycleOf(currentPlan) : null;
  // A plan whose setup run is in flight is not replaced underneath it; the
  // API refuses too, this only says so before anyone tries.
  const runInFlight = lifecycle === 'running' || lifecycle === 'run_pending';
  const run = currentPlan?.execution ?? null;

  return (
    <div className="card onboarding-plan-card">
      <SectionHeader
        title={`Set up ${environment.name} on a server`}
        actions={
          currentPlan ? (
            <span className="summary-line">
              <span className="badge">Plan v{currentPlan.version}</span>
              <StatusBadge kind="planLifecycle" value={lifecycle} testId="plan-lifecycle" />
            </span>
          ) : undefined
        }
      />

      <div className="plan-card__body">
        {actionError && <ErrorNotice error={actionError} />}

        {/* Server selector */}
        <div className="plan-card__server">
          <div className="plan-card__server-field field">
            <label htmlFor="target-server-select" className="field__label">
              Server
            </label>
            <Select
              id="target-server-select"
              value={selectedServerId}
              onChange={(e) => setSelectedServerId(e.target.value)}
              disabled={generating || approving}
            >
              {servers.length === 0 ? (
                <option value="">No servers connected yet</option>
              ) : (
                servers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.hostname ? ` (${s.hostname})` : ''} — {serverStatusLabel[s.status] ?? s.status}, {readinessStatusLabel[s.readiness_status] ?? s.readiness_status}
                  </option>
                ))
              )}
            </Select>
            {servers.length === 0 ? (
              <p className="field__hint">
                Connect a server first (Servers → Connect server).
              </p>
            ) : serverNotReady ? (
              <p className="field__hint">
                {selectedServer?.status !== 'online'
                  ? 'Agent offline; the plan uses the last server check.'
                  : 'Server not checked yet. Run the server check first.'}
              </p>
            ) : null}
          </div>

          <button
            type="button"
            className={`button ${currentPlan ? 'button--secondary' : 'button--primary'}`}
            onClick={() => handleGeneratePlan()}
            disabled={generating || servers.length === 0 || runInFlight}
            title={runInFlight ? 'The setup run of this plan is in progress. Let it finish or cancel it before planning again.' : undefined}
          >
            {generating ? 'Planning' : currentPlan ? 'Plan again' : 'Plan the setup'}
          </button>
          {isApproved && !runInFlight ? (
            <p className="field__hint plan-card__full">
              Planning again replaces this approved plan with a new version that needs its own approval.
            </p>
          ) : null}
        </div>

        {currentPlan ? (
          <>
            {/* Once the setup has run, the review is a record: it stays one
                click away instead of making the page 3,800 px tall (audit G4).
                Before that, the person is reviewing it, so it stays open. */}
            {/* Once approved the plan is decided: its review is a record one
                click away, so the run and its outcome lead (UX-10). Before
                approval the person is reviewing it, so it stays open. */}
            <PlanReview collapsed={Boolean(lifecycle) && lifecycle !== 'awaiting_approval'}>
            {/* Repository analysis */}
            <RepositoryAnalysisSection plan={currentPlan} />

            {/* Application capabilities: what the code needs, discovered */}
            {currentPlan.repository_analysis.is_magento ? (
              <CatalogSearchSection
                capability={catalogSearchOf(currentPlan.repository_analysis)}
                component={currentPlan.components.find((c) => c.component === 'catalog_search') ?? null}
                estimate={currentPlan.resource_estimate ?? null}
                drift={currentPlan.capability_drift}
              />
            ) : null}

            {/* Strategy */}
            <Subsection title="How Magento will run on this server">
              <OptionGroup label="How Magento will run">
                {currentPlan.strategy_evaluations.map((evalItem) => {
                  const isBlocked = evalItem.status === 'blocked';
                  return (
                    <OptionCard
                      key={evalItem.strategy}
                      title={strategyLabel[evalItem.strategy] ?? humanize(evalItem.strategy)}
                      badge={<StatusBadge kind="capability" value={evalItem.status} label={evalItem.status === 'unknown' ? 'Not verified' : undefined} />}
                      description={strategyDescription[evalItem.strategy] ?? evalItem.reason}
                      note={evalItem.cons && evalItem.cons.length > 0 ? evalItem.cons.join(' ') : undefined}
                      noteTone={isBlocked ? 'danger' : 'warning'}
                      selected={currentPlan.strategy === evalItem.strategy}
                      selectedLabel="Selected for this plan"
                      disabled={isBlocked || approving || isApproved}
                      onSelect={() => setSelectedStrategy(evalItem.strategy)}
                    />
                  );
                })}
              </OptionGroup>
            </Subsection>

            <PerformanceStack plan={currentPlan} />

            {/* Components */}
            <div>
              <h3 className="plan-card__h3">What Commerce Ops will do on the server</h3>
              <Table label="What Commerce Ops will do on the server" wrapperClassName="table-frame">
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Today</th>
                    <th>Plan</th>
                    <th>Owned by</th>
                    <th>Why</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPlan.components.map((comp, index) => (
                    // A plan lists one row per frontend, so the component
                    // type alone is not unique in a multi-frontend project.
                    <tr key={`${comp.component}-${String(comp.details?.application_id ?? comp.details?.name ?? index)}`}>
                      <th scope="row">{componentDisplayName(comp)}</th>
                      <td>{currentStateLabel[comp.current_state] ?? humanize(comp.current_state)}</td>
                      <td>
                        <StatusBadge kind="planAction" value={comp.proposed_action} />
                      </td>
                      <td>
                        <StatusBadge kind="ownership" value={comp.future_ownership} />
                      </td>
                      <td className="muted">{comp.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Risks */}
            {currentPlan.risks.length > 0 && (
              <div>
                <h3 className="plan-card__h3">Things to know before you approve</h3>
                <div className="stack">
                  {currentPlan.risks.map((risk, idx) => (
                    <Callout
                      key={idx}
                      tone={risk.level === 'high' || risk.level === 'blocking' ? 'danger' : risk.level === 'warning' ? 'warning' : 'info'}
                      title={risk.title}
                      actions={<StatusBadge kind="planRisk" value={risk.level} />}
                    >
                      {risk.description}
                    </Callout>
                  ))}
                </div>
              </div>
            )}

            {/* Required inputs — only while the plan can still change */}
            {currentPlan.required_inputs.length > 0 && !isApproved && (
              <form onSubmit={handleGeneratePlan} className="plan-card__inputs">
                <h3 className="plan-card__h3">Needs your input</h3>

                <div className="form-grid">
                  <Field
                    label={isHeadless ? 'Magento hostname' : 'Store domain'}
                    hint={
                      isHeadless
                        ? 'e.g. api.example.com. Point its DNS at the server.'
                        : 'e.g. store.example.com. Point its DNS at the server.'
                    }
                  >
                    <Input
                      type="text"
                      value={productionDomain}
                      onChange={(e) => setProductionDomain(e.target.value)}
                      placeholder={isHeadless ? 'api.example.com' : 'store.example.com'}
                      required
                    />
                  </Field>
                </div>

                <div className="plan-card__form-actions">
                  <button type="submit" className="button button--primary" disabled={generating || approving}>
                    {generating ? 'Planning' : 'Save and plan again'}
                  </button>
                </div>
              </form>
            )}

            {/* Steps */}
            <Subsection title="Steps the setup will run">
              <StepList
                label="Steps the setup will run"
                steps={currentPlan.steps.map((step) => ({
                  key: step.id,
                  title: step.title,
                  description: step.description,
                  status: step.risk && step.risk !== 'info' ? { kind: 'planRisk' as const, value: step.risk } : undefined,
                  details: <code>{step.action_type}</code>,
                }))}
              />
            </Subsection>

            </PlanReview>

            {/* Approval and run: the plan's lifecycle, read from the API */}
            <div
              className="plan-run"
              aria-live="polite"
              data-testid="plan-run"
              data-lifecycle={lifecycle ?? ''}
            >
              <div className="plan-run__summary">
                <PlanRunSummary plan={currentPlan} lifecycle={lifecycle!} />
              </div>

              <div className="plan-run__actions">
                {lifecycle === 'awaiting_approval' ? (
                  <button type="button" className="button button--primary" onClick={handleApprovePlan} disabled={approving || generating}>
                    {approving ? 'Approving' : 'Approve plan'}
                  </button>
                ) : null}

                {(lifecycle === 'failed' || lifecycle === 'cancelled' || lifecycle === 'completed') && run && onOpenExecution ? (
                  <button type="button" className="button button--secondary" onClick={() => onOpenExecution(run.id)} data-testid="plan-open-run">
                    {lifecycle === 'completed' ? 'View the run' : lifecycle === 'failed' ? 'Open the failed run' : 'Open the cancelled run'}
                  </button>
                ) : null}

                {(lifecycle === 'running' || lifecycle === 'run_pending') && run && onOpenExecution ? (
                  <button type="button" className="button button--primary" onClick={() => onOpenExecution(run.id)} data-testid="plan-open-run">
                    {lifecycle === 'run_pending' ? 'Open the run to start it' : 'Follow the run'}
                  </button>
                ) : null}

                {(lifecycle === 'approved' || lifecycle === 'failed' || lifecycle === 'cancelled') && onLaunchExecution ? (
                  <button type="button" className="button button--primary" onClick={handleLaunch} disabled={launching || generating} data-testid="plan-run-action">
                    {launching ? 'Starting' : lifecycle === 'approved' ? 'Run plan' : 'Run plan again'}
                  </button>
                ) : null}
              </div>
              {lifecycle === 'failed' || lifecycle === 'cancelled' ? (
                <ul className="plan-run__guide" data-testid="plan-run-guide" aria-label="Which action to use">
                  <li>
                    <strong>{lifecycle === 'failed' ? 'Open the failed run' : 'Open the cancelled run'}</strong>: where it stopped and why; retry that step.
                  </li>
                  <li>
                    <strong>Run plan again</strong>: a new run of the same approved plan, once the cause is fixed.
                  </li>
                  <li>
                    <strong>Plan again</strong>: replaces the approved plan and needs approval again.
                  </li>
                </ul>
              ) : null}
              {launchError ? (
                <div className="plan-card__full" data-testid="plan-run-launch-error">
                  <ErrorNotice error={launchError} />
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <QuietEmpty testId="no-plan">No plan yet</QuietEmpty>
        )}
      </div>
    </div>
  );
}

/** editionName turns the analyser's edition token into the product name. */
function editionName(edition?: string): string {
  switch (edition) {
    case 'Community':
      return 'Magento Open Source';
    case 'Enterprise':
      return 'Adobe Commerce';
    case 'Mage-OS':
      return 'Mage-OS';
    case 'Custom/Modular':
      return 'Magento (framework only)';
    default:
      return edition ? `Magento (${edition})` : 'Magento';
  }
}

/** magentoSummary states the release the repository pins, or the constraint
 * it declares, without inventing an exact version. */
/** The storefront the repository shows: Luma, Hyvä (with its themes) or undetermined. */
function storefrontSummary(d: StorefrontDetection): string {
  if (d.storefront === 'hyva') {
    const v = d.hyva_packages?.find((p) => p.name === 'hyva-themes/magento2-theme-module')?.version;
    return v ? `Hyvä ${v}` : 'Hyvä';
  }
  if (d.storefront === 'luma') return 'Luma (Magento’s own themes)';
  return 'Not determined';
}

function storefrontDetail(d: StorefrontDetection): string {
  if (d.storefront === 'undetermined') return d.ambiguities?.[0] ?? '';
  const builds = (d.themes ?? []).filter((t) => t.build).map((t) => `${t.code} (${t.build?.package_manager}, Node ${t.build?.node_major ?? '?'})`);
  return builds.length ? `Tailwind built for ${builds.join(', ')}` : '';
}

function magentoSummary(a: RepositoryAnalysis): { primary: string; secondary?: string } {
  if (!a.is_magento) return { primary: 'Not detected' };
  const name = editionName(a.magento_edition);
  if (a.magento_version) {
    return { primary: `${name} ${a.magento_version}`, secondary: a.magento_version_source ? `from ${a.magento_version_source}` : undefined };
  }
  if (a.magento_version_constraint) {
    return { primary: name, secondary: `composer constraint ${a.magento_version_constraint}` };
  }
  return { primary: name, secondary: 'release not determined from the repository' };
}

/** infrastructureFiles lists the deployment-related files the analyser saw. */
function infrastructureFiles(a: RepositoryAnalysis): string[] {
  const out: string[] = [];
  const files = a.provenance?.files_read ?? a.identified_files ?? [];
  for (const f of files) {
    if (/^(docker-compose|compose)\.ya?ml$/.test(f)) out.push(f);
  }
  for (const f of a.dockerfiles ?? []) if (!out.includes(f)) out.push(f);
  if (a.magento_cloud_config_detected) out.push('.magento.env.yaml (Adobe Commerce Cloud)');
  for (const f of a.deployment_scripts ?? []) if (!out.includes(f)) out.push(f);
  return out;
}

/** catalogSearchOf returns the discovered capability, or a placeholder for
 * plans made before catalog search discovery existed. */
export function catalogSearchOf(a: RepositoryAnalysis): ApplicationCapability {
  const found = (a.capabilities ?? []).find((c) => c.key === 'catalog_search');
  if (found) return found;
  return {
    key: 'catalog_search',
    status: 'unresolved',
    confidence: 'unknown',
    source: 'static_analysis',
    provider: 'unknown',
    provider_name: 'Not determined',
    implementation: 'unknown',
    external_service_required: false,
    magento_native_support: 'unknown',
    repository_support: 'unknown',
    compatibility: 'unknown',
    evidence: [],
    warnings: ['This plan was made before catalog search discovery existed. Plan again to analyse the repository.'],
    detected_at: '',
  };
}

function catalogSearchSummary(a: RepositoryAnalysis): string {
  const cap = catalogSearchOf(a);
  if (cap.status === 'conflict') return 'Store and repository disagree';
  if (cap.status === 'unresolved') return cap.engine_identifier ? `Custom engine ${cap.engine_identifier} (not identified)` : 'Not determined';
  return `Detected: ${cap.provider_name}`;
}

function shortSha(sha?: string): string {
  return sha ? sha.slice(0, 7) : '';
}

/** RepositoryAnalysisSection shows what the plan knows about the repository
 * and — first — where that knowledge comes from: the branch and commit
 * that were read, or the reason nothing was read and defaults were assumed.
 * Values the analyser derived rather than read are marked as inferred. */
export function RepositoryAnalysisSection({ plan }: { plan: OnboardingPlan }) {
  const a = plan.repository_analysis;
  const p = a.provenance;
  const assumed = a.source === 'assumed_defaults';
  const magento = magentoSummary(a);
  const infra = infrastructureFiles(a);
  const committedSecrets = [a.env_php_committed ? 'app/etc/env.php' : null, a.auth_json_committed ? 'auth.json' : null].filter(Boolean) as string[];
  const analyzedAt = p?.analyzed_at ? new Date(p.analyzed_at) : null;

  return (
    <section className="repo-analysis subsection">
      <SectionHeader
        level={3}
        title="What the repository needs"
        actions={
          <span data-testid="repository-analysis-source">
            <StatusBadge kind="repositoryAnalysis" value={assumed ? 'assumed_defaults' : a.is_magento ? 'repository' : 'not_magento'} />
          </span>
        }
      />

      {assumed ? (
        <Callout tone="warning" title={`Why: ${labelFor(repositoryFallbackReasonLabel, p?.fallback_reason) || 'the repository was not read'}`} testId="repository-analysis-fallback">
          <span>
            {p?.fallback_message ||
              'Repository not read; the values below are Magento 2.4 defaults, not your composer.json.'}{' '}
            The actual requirements are checked when the first deployment builds the release.
          </span>
        </Callout>
      ) : (
        <p className="type-meta" data-testid="repository-analysis-provenance">
          {p?.fetch_method === 'provided'
            ? 'Analyzed from files supplied with the request'
            : `Read from ${p?.branch ? `branch ${p.branch}` : 'the repository’s default branch'}${p?.commit_sha ? ` at commit ${shortSha(p.commit_sha)}` : ''}`}
          {analyzedAt ? `, ${analyzedAt.toLocaleString()}` : ''}
        </p>
      )}

      <div className="fact-grid">
        <div>
          <span className="fact-block__label">Magento</span>
          <strong>{magento.primary}</strong>
          {magento.secondary ? <span className="fact-block__sub">{magento.secondary}</span> : null}
        </div>
        <div>
          <span className="fact-block__label">PHP</span>
          <code>{a.required_php_version || 'Not declared'}</code>
          {a.required_php_extensions && a.required_php_extensions.length > 0 ? (
            <span className="fact-block__sub">Extensions: {a.required_php_extensions.join(', ')}</span>
          ) : null}
        </div>
        <div>
          <span className="fact-block__label">Catalog search</span>
          <strong data-testid="repository-analysis-search">{catalogSearchSummary(a)}</strong>
        </div>
        <div>
          <span className="fact-block__label">Database</span>
          <strong>{a.database_requirement?.value || 'Not determined'}</strong>
          {a.database_requirement ? (
            <span className="fact-block__sub">Inferred: {a.database_requirement.source}</span>
          ) : null}
        </div>
        <div>
          <span className="fact-block__label">Module configuration</span>
          {a.config_php_committed ? (
            <>
              <strong>
                {a.enabled_modules ?? a.modules_count} enabled{a.disabled_modules ? `, ${a.disabled_modules} disabled` : ''}
              </strong>
              <span className="fact-block__sub">app/etc/config.php is committed</span>
            </>
          ) : (
            <>
              <strong>{assumed ? 'Unknown' : 'Not committed'}</strong>
              <span className="fact-block__sub">
                {assumed ? 'app/etc/config.php was not read' : 'app/etc/config.php is not in the repository'}
              </span>
            </>
          )}
        </div>
        <div>
          <span className="fact-block__label">Deployment files</span>
          <strong>{infra.length > 0 ? infra.join(', ') : assumed ? 'Unknown' : 'None found'}</strong>
          {infra.length > 0 ? (
            <span className="fact-block__sub">Not used by the Commerce Ops managed stack</span>
          ) : null}
        </div>
        <div>
          <span className="fact-block__label">Project type</span>
          <strong>{a.headless ? 'Headless (Magento + frontend)' : 'Magento with its own storefront'}</strong>
        </div>
        {a.storefront ? (
          <div data-testid="plan-storefront">
            <span className="fact-block__label">Storefront</span>
            <strong>{storefrontSummary(a.storefront)}</strong>
            {storefrontDetail(a.storefront) ? <span className="fact-block__sub">{storefrontDetail(a.storefront)}</span> : null}
          </div>
        ) : null}
        {a.frontend_component && (
          <div>
            <span className="fact-block__label">Frontend</span>
            <span className="badge">
              {a.frontend_component.framework.toUpperCase()} (Port {a.frontend_component.port})
            </span>
          </div>
        )}
        {committedSecrets.length > 0 ? (
          <div>
            <span className="fact-block__label">Secrets in the repository</span>
            <strong className="plan-card__danger">{committedSecrets.join(', ')}</strong>
            <span className="fact-block__sub">Not read by Commerce Ops</span>
          </div>
        ) : null}
        <div>
          <span className="fact-block__label">Server is</span>
          <strong>
            {plan.server_usage === 'shared' ? 'Shared with other software' : plan.server_usage === 'dedicated' ? 'Dedicated to Commerce Ops' : 'Not determined'}
          </strong>
        </div>
      </div>
    </section>
  );
}

/** PlanRunSummary says where the plan stands in words: approved and waiting
 * for its run, running, finished, or failed with the run's own error. */
function PlanRunSummary({ plan, lifecycle }: { plan: OnboardingPlan; lifecycle: PlanLifecycle }) {
  const run = plan.execution ?? null;
  const approvedBy = `Approved by ${plan.approved_by || 'a team member'} · ${formatWhen(plan.approved_at || plan.updated_at)}.`;
  switch (lifecycle) {
    case 'awaiting_approval':
      return (
        <>
          <strong className="plan-run__title">Ready for your approval</strong>
          <span className="plan-run__detail">Nothing runs on the server until you run the plan.</span>
        </>
      );
    case 'approved':
      return (
        <>
          <strong className="plan-run__title">Plan approved — not run yet</strong>
          <span className="plan-run__detail">
            {approvedBy} Running the plan creates the environment’s network and Commerce Ops-owned services; the first
            deployment installs Magento.
          </span>
        </>
      );
    case 'run_pending':
      return (
        <>
          <strong className="plan-run__title">The run was created but not started</strong>
          <span className="plan-run__detail">Created {formatWhen(run?.created_at)}</span>
        </>
      );
    case 'running':
      return (
        <>
          <strong className="plan-run__title">Setup is running on the server</strong>
          <span className="plan-run__detail">Started {formatWhen(run?.started_at || run?.created_at)}</span>
        </>
      );
    case 'completed':
      return (
        <>
          <strong className="plan-run__title plan-run__title--success">Setup completed</strong>
          <span className="plan-run__detail">Finished {formatWhen(run?.completed_at)}</span>
        </>
      );
    case 'failed':
    case 'cancelled':
      return (
        <>
          <strong className={`plan-run__title${lifecycle === 'failed' ? ' plan-run__title--danger' : ''}`}>
            {lifecycle === 'failed' ? 'The setup run failed' : 'The setup run was cancelled'}
          </strong>
          <span className="plan-run__detail" data-testid="plan-run-error">
            {run?.error_message ? `${run.error_message}${run.error_code ? ` (${run.error_code})` : ''}. ` : 'Cause not recorded. '}
            The approved plan is unchanged.
          </span>
        </>
      );
    case 'blocked':
      return (
        <>
          <strong className="plan-run__title plan-run__title--danger">Not possible on this server as it is</strong>
          <span className="plan-run__detail">
            {plan.risks.find((r) => r.level === 'blocking')?.description ?? 'Fix what blocks it (see above), check the server again, then plan again.'}
          </span>
        </>
      );
    case 'superseded':
      return (
        <>
          <strong className="plan-run__title">Superseded</strong>
          <span className="plan-run__detail">Replaced by a newer plan</span>
        </>
      );
    default:
      return (
        <>
          <strong className="plan-run__title">Needs information</strong>
          <span className="plan-run__detail">Plan again once the input is answered.</span>
        </>
      );
  }
}

function PlanReview({ collapsed, children }: { collapsed: boolean; children: ReactNode }) {
  if (!collapsed) return <>{children}</>;
  return (
    <details className="disclosure plan-review" data-testid="plan-review">
      <summary>What the plan decided</summary>
      <div className="plan-review__body">{children}</div>
    </details>
  );
}
